import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PlusIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import StatusBadge from '../../../components/common/StatusBadge';
import Pagination from '../../../components/common/Pagination';
import { useAuth } from '../../../contexts/AuthContext';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import { getJobs, deleteJob } from '../../../services/jobsService';

const Jobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showTodayOnly, setShowTodayOnly] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [todayJobs, setTodayJobs] = useState([]);
  const [todayLoading, setTodayLoading] = useState(false);
  const { user } = useAuth();
  const role = user?.role;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);

  useEffect(() => {
    fetchJobs();
  }, [search, statusFilter, currentPage, startDate, endDate]);

  useEffect(() => {
    if (showTodayOnly) {
      fetchTodayJobs();
    }
  }, [showTodayOnly]);

  const fetchJobs = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      params.append('page', currentPage);

      const response = await getJobs(Object.fromEntries(params));
      setJobs(response.jobs || []);
      setTotalPages(response.totalPages || 1);
      setTotal(response.total || 0);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTodayJobs = async () => {
    try {
      setTodayLoading(true);
      const today = new Date().toISOString().split('T')[0];
      const response = await getJobs({
        startDate: today,
        endDate: today,
        limit: 1000
      });
      setTodayJobs(response.jobs || []);
    } catch (error) {
      console.error('Error fetching today\'s jobs:', error);
      setTodayJobs([]);
    } finally {
      setTodayLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const resetToFirstPage = () => {
    setCurrentPage(1);
  };

  // Reset to first page when search or filter changes
  useEffect(() => {
    resetToFirstPage();
  }, [search, statusFilter, startDate, endDate]);

  const askDelete = (job) => {
    setJobToDelete(job);
    setConfirmOpen(true);
  };

  const deleteJob = async () => {
    if (!jobToDelete) return;
    try {
      await deleteJob(jobToDelete._id);
      setConfirmOpen(false);
      setJobToDelete(null);
      fetchJobs();
    } catch (e) {
      setConfirmOpen(false);
      setJobToDelete(null);
      console.error('Delete job failed', e);
    }
  };

  if (loading || (showTodayOnly && todayLoading)) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Jobs</h2>
        {['admin', 'manager', 'sales_manager'].includes(role) && (
          <Link
            to="/jobs/new"
            className="btn-primary flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            New Job
          </Link>
        )}
      </div>

      {/* Today's Jobs Toggle */}
      <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 shadow-sm">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={showTodayOnly}
            onChange={(e) => setShowTodayOnly(e.target.checked)}
            className="rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500 w-5 h-5"
          />
          <span className="text-sm font-semibold text-indigo-900">Show Today's Jobs Only</span>
        </label>
        {showTodayOnly && (
          <span className="px-3 py-1 text-xs font-semibold bg-white text-indigo-700 rounded-full shadow-sm border border-indigo-200">
            {todayJobs.length} jobs today
          </span>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="flex-1">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search jobs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-field pl-10"
            />
          </div>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="input-field w-48"
        >
          <option value="">All Status</option>
          <option value="design">Design</option>
          <option value="approved">Approved</option>
          <option value="printed">Printed</option>
          <option value="ready_for_collection">Ready for Collection</option>
          <option value="collected">Collected</option>
        </select>
      </div>

      {/* Date Filters */}
      <div className="flex gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="input-field"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="input-field"
          />
        </div>
        <div className="flex items-end">
          <button
            onClick={() => {
              setStartDate('');
              setEndDate('');
              setShowTodayOnly(false);
            }}
            className="btn-secondary"
          >
            Clear Dates
          </button>
        </div>
      </div>

      {/* Jobs List */}
      <div className={`relative overflow-hidden rounded-2xl shadow-lg border ${
        showTodayOnly 
          ? 'border-amber-200 bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50' 
          : 'border-indigo-200 bg-gradient-to-br from-indigo-50 via-blue-50 to-purple-50'
      }`}>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <h3 className={`text-lg font-bold ${showTodayOnly ? 'text-amber-900' : 'text-indigo-900'}`}>
              {showTodayOnly ? "Today's Jobs" : 'All Jobs'}
            </h3>
            {showTodayOnly && (
              <span className="px-3 py-1 text-xs font-semibold bg-amber-100 text-amber-800 rounded-full border border-amber-300">
                {todayJobs.length} jobs
              </span>
            )}
          </div>
          <div className="overflow-x-auto bg-white rounded-xl shadow-inner">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className={`${showTodayOnly ? 'bg-gradient-to-r from-amber-50 to-yellow-50' : 'bg-gradient-to-r from-indigo-50 to-blue-50'}`}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-bold uppercase tracking-wider ${showTodayOnly ? 'text-amber-800' : 'text-indigo-800'}`}>
                    Job ID
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-bold uppercase tracking-wider ${showTodayOnly ? 'text-amber-800' : 'text-indigo-800'}`}>
                    Customer
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-bold uppercase tracking-wider ${showTodayOnly ? 'text-amber-800' : 'text-indigo-800'}`}>
                    Total Price
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-bold uppercase tracking-wider ${showTodayOnly ? 'text-amber-800' : 'text-indigo-800'}`}>
                    Status
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-bold uppercase tracking-wider ${showTodayOnly ? 'text-amber-800' : 'text-indigo-800'}`}>
                    Due Date
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-bold uppercase tracking-wider ${showTodayOnly ? 'text-amber-800' : 'text-indigo-800'}`}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {(showTodayOnly ? todayJobs : jobs).map((job, index) => (
                  <tr key={job._id} className={`transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${
                    showTodayOnly ? 'hover:bg-amber-50' : 'hover:bg-indigo-50'
                  }`}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {job.jobId}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {job.customer?.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    KSh {(job.totalPrice || 0).toLocaleString('en-KE')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <StatusBadge status={job.status} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(job.expectedDueDate).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <Link
                      to={`/jobs/${job._id}`}
                      className="text-primary-600 hover:text-primary-900 mr-3"
                    >
                      View
                    </Link>
                    {['admin', 'manager', 'sales_manager'].includes(role) && (
                      <Link
                        to={`/jobs/${job._id}/edit`}
                        className="text-primary-600 hover:text-primary-900 mr-3"
                      >
                        Edit
                      </Link>
                    )}
                    {role === 'admin' && (
                      <button
                        className="text-red-600 hover:text-red-800"
                        onClick={() => askDelete(job)}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </div>
          {(showTodayOnly ? todayJobs : jobs).length === 0 && (
            <div className="text-center py-8 text-gray-500">
              {showTodayOnly ? 'No jobs found for today' : 'No jobs found'}
            </div>
          )}
        </div>
      </div>

      {/* Pagination - Only show when not viewing today's jobs */}
      {!showTodayOnly && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          total={total}
          onPageChange={handlePageChange}
        />
      )}

      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={deleteJob}
        title="Delete Job"
        message={`Are you sure you want to delete job ${jobToDelete?.jobId}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default Jobs; 